import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type {
  ResumeConfirmRequest,
  ResumeConfirmResponse,
  ResumeContinueResponse,
} from '@rheora/enrollment-contract';

import { funnelQueryKeys } from './funnel.queries';
import {
  postResumeConfirm,
  postResumeConfirmSkip,
  postResumeContinue,
  postResumeContinueSkip,
} from './resume.fetchers';

/**
 * E-CRM04 — the Continue press: redeem the held resume token. Either outcome
 * changes the funnel session, so the snapshot is invalidated.
 */
export function useResumeContinueMutation(): UseMutationResult<ResumeContinueResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postResumeContinue(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}

/** "Start over instead" on /resume/continue. */
export function useResumeContinueSkipMutation(): UseMutationResult<
  ResumeContinueResponse,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postResumeContinueSkip(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}

/**
 * E-CRM04 — confirm the phone number to release a held resume. Either outcome
 * changes the funnel session (seeded, or the held lead discarded), so the
 * snapshot is invalidated.
 */
export function useResumeConfirmMutation(): UseMutationResult<
  ResumeConfirmResponse,
  Error,
  ResumeConfirmRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ResumeConfirmRequest) => postResumeConfirm(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}

/** "Start over instead". */
export function useResumeConfirmSkipMutation(): UseMutationResult<
  ResumeConfirmResponse,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postResumeConfirmSkip(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}
